import type { PaymentInstrumentSnapshot } from '../paymentInstrument'

type PaymentInstrumentLabelInput = Pick<
  PaymentInstrumentSnapshot,
  'cardName' | 'cardLast4' | 'cardType' | 'paymentMethodType' | 'walletType'
>

const METHOD_TYPE_LABELS: Record<string, string> = {
  card: 'Card',
  link: 'Link',
  us_bank_account: 'Bank',
}

function toNormalizedToken(value?: string | null): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed) return undefined
  return trimmed.toLowerCase().replace(/[\s-]+/g, '_')
}

function toTitleCase(value: string): string {
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
}

function formatWalletType(walletType?: string | null): string | undefined {
  const normalized = toNormalizedToken(walletType)
  if (!normalized) return undefined
  if (normalized === 'apple_pay') return 'Apple Pay'
  if (normalized === 'google_pay') return 'Google Pay'
  return toTitleCase(normalized)
}

function formatCardType(cardType?: string | null): string | undefined {
  const normalized = toNormalizedToken(cardType)
  if (!normalized) return undefined
  if (normalized === 'link') return 'Link'
  if (normalized === 'us_bank_account') return 'Bank'
  return toTitleCase(normalized)
}

function formatPaymentMethodType(
  paymentMethodType?: string | null
): string | undefined {
  const normalized = toNormalizedToken(paymentMethodType)
  if (!normalized) return undefined
  if (METHOD_TYPE_LABELS[normalized]) return METHOD_TYPE_LABELS[normalized]
  return toTitleCase(normalized)
}

export function getNormalizedPaymentLast4(
  last4?: string | null
): string | undefined {
  const digitsOnly = (last4 ?? '').replace(/\D/g, '')
  if (!digitsOnly || digitsOnly.length < 4) return undefined
  if (/^0+$/.test(digitsOnly)) return undefined
  return digitsOnly.slice(-4)
}

export function getPaymentInstrumentDisplayLabel(
  input: PaymentInstrumentLabelInput
): string | undefined {
  const last4 = getNormalizedPaymentLast4(input.cardLast4)
  const cardholderName = input.cardName?.trim() || undefined
  const walletTypeLabel = formatWalletType(input.walletType)
  const cardTypeLabel = formatCardType(input.cardType)
  const paymentMethodTypeLabel = formatPaymentMethodType(
    input.paymentMethodType
  )

  if (walletTypeLabel)
    return [walletTypeLabel, last4].filter(Boolean).join(' • ')
  if (cardholderName && last4) return `${cardholderName} • ${last4}`
  if (cardTypeLabel && last4) return `${cardTypeLabel} • ${last4}`
  if (paymentMethodTypeLabel && paymentMethodTypeLabel !== 'Card') {
    return [paymentMethodTypeLabel, last4].filter(Boolean).join(' • ')
  }
  if (cardholderName) return cardholderName
  if (cardTypeLabel) return cardTypeLabel
  if (last4) return last4
  return paymentMethodTypeLabel
}
